/**
 * Mirrors `GET /recognitions/leaderboard`'s wire contract (api/app/shared/schemas/leaderboard.schemas.ts,
 * api/app/shared/constants/shared.ts) -- 923-core-context context/engineering/spark-rewards/phase-38-implementation-plan.md.
 *
 * D3: web's own `LEADERBOARD_BOARD` deliberately omits `TEAM` entirely, unlike the api's own enum
 * (which has RECEIVED/GIVEN/CROWNS/TEAM) -- pavel's decision to never render a team board on web. This
 * turns that decision into a compile-time guarantee: web code cannot construct a request carrying
 * `board: "team"` because the enum this type is built from has no such member. A runtime test (the
 * container never issues a request with `board: "team"`) still exists as a second, independent layer.
 */
export enum LEADERBOARD_BOARD {
  RECEIVED = "received",
  GIVEN = "given",
  CROWNS = "crowns",
}

/** Byte-for-byte the api's own `LEADERBOARD_PERIOD` (api/app/shared/constants/shared.ts). */
export enum LEADERBOARD_PERIOD {
  WEEK = "Week",
  MONTH = "Month",
  QUARTER = "Quarter",
  ALL_TIME = "All-time",
}

/** A leaderboard row's `user` -- `userMainInfoSchema` on the wire: `{ id, first_name, last_name,
 * avatar_url }` (google-profile-pictures-plan.md Phase 1 added `avatar_url` to this shared schema, so
 * every embedded user built from it -- ranked rows, crowns, suggested collaborators -- carries it). */
export interface LeaderboardUser {
  id: number;
  first_name: string;
  last_name: string;
  avatar_url?: string | null;
}

/** `LeaderboardRow` on the wire (received/given boards). */
export interface LeaderboardEntry {
  user_id: number;
  total_score: number;
  user: LeaderboardUser;
  /**
   * L3 (923-core-context context/engineering/spark-rewards/phase-51-leaderboard-to-design.md): the core value this person scored the most points in, over
   * the SAME window this board already applies -- an argmax per user, computed api-side. `null` is a
   * real, valid state (no qualifying recognitions to pick an argmax from), never a missing-data
   * placeholder -- the per-row value chip (RankRow.tsx) renders NO chip for it, not a default one.
   */
  top_core_value_id: number | null;
}

/**
 * A crown row's own core-value shape -- deliberately NOT the shared `CoreValue` model reused wholesale
 * (D4/D5 of the plan): `CoreValue.icon_url` is typed `string`, but `recognition.schemas.ts:25` is
 * `z.string().min(1).nullable().optional()` on the wire, i.e. `string | null | undefined` (stripped to
 * nullable-only by `leaderboard.schemas.ts`'s own `leaderboardCoreValueSchema`, never `undefined` once
 * present). Recorded, not fixed here: the shared `CoreValue` model's own `icon_url` typing looks wrong
 * against the real schema too, but other already-shipped consumers may have their own (undocumented)
 * assumptions about it -- out of scope for this phase.
 */
export interface LeaderboardCoreValue {
  id: number;
  title: string;
  description: string;
  icon_url: string | null;
}

/**
 * `ValueCrown` on the wire (`leaderboard.schemas.ts`'s `valueCrownRowSchema`) -- `user` is nullable, not
 * optional: a core value with no qualifying recognitions in the window sends `user: null` explicitly,
 * never omits the key.
 */
export interface LeaderboardCrown {
  core_value_id: number;
  coreValue: LeaderboardCoreValue;
  user: LeaderboardUser | null;
  total_score: number;
}

/**
 * The logged-in viewer's own position on a ranked board (overview-leaderboard-card-plan.md Part 1,
 * api's `leaderboard.service.ts`) -- ORDINAL ranking (1, 2, 3, 4), not competition ranking: every tie
 * is broken by the board's own tiebreak column, so no two viewers ever share a rank.
 *
 * `null` (the whole object, not `rank: 0`) when the viewer has no score in this window at all.
 * `points_to_next_rank` is `null` ONLY at rank 1 (nobody to overtake) -- it is never `0`, because
 * overtaking a same-score neighbour still costs 1 point once the tiebreak is accounted for. It is the
 * cost to overtake the row immediately ABOVE, already including that tiebreak -- not the raw score
 * gap, and not the gap to the leader -- so the web must render it as-is and never adjust it.
 */
export interface LeaderboardViewer {
  rank: number;
  total_score: number;
  points_to_next_rank: number | null;
}

/**
 * `getPeriodResetWindow` on the wire (923-core-context context/engineering/spark-rewards/phase-51-leaderboard-to-design.md's own L6/L7,
 * `api/app/shared/schemas/leaderboard.schemas.ts`'s `leaderboardWindowSchema`) -- the period's own
 * boundaries, `startsAt`/`resetsAt` (deliberately NOT `from`/`to`: the api's own `getPeriodWindow` uses
 * that pair for a DIFFERENT meaning, see that function's own JSDoc). `resetsAt` is always in the
 * FUTURE relative to when the response was built -- the next period has not started yet. `null` for
 * ALL_TIME, which has no window to reset. L7's reset line ("Resets in 18 days · new quarter 1 Oct")
 * renders from THIS field -- never a client-computed period boundary (see `leaderboardResetCopy.ts`'s
 * own JSDoc for why deriving the boundary locally would be a second, independent writer of a rule the
 * api already owns).
 */
export interface LeaderboardWindow {
  startsAt: string;
  resetsAt: string;
}

/**
 * `GET /recognitions/leaderboard`'s response for the ranked boards (received/given). `viewer` is added
 * here ONLY -- `LeaderboardCrownsResponse` below is a separate interface and deliberately gains no such
 * field (the api's `crowns`/`team` branches carry no `viewer` key either).
 *
 * `window` (923-core-context context/engineering/spark-rewards/phase-51-leaderboard-to-design.md L6/L7) mirrors the api's own `rankedBoardResultFields` --
 * see `LeaderboardWindow`'s own comment.
 *
 * `pendingApproval` (same api field, L6) is modelled here for accuracy -- the wire response really does
 * carry it -- but has no consumer anywhere on web yet (L7's own frame shows a WEEKLY BUDGET footnote,
 * a separate and unbuilt piece of copy, not this count). Deliberately left unpicked by
 * `getLeaderboard`'s action payload (`actions.tsx`) rather than given an invented home.
 */
export interface LeaderboardResultsResponse {
  board: LEADERBOARD_BOARD.RECEIVED | LEADERBOARD_BOARD.GIVEN;
  period: LEADERBOARD_PERIOD;
  results: LeaderboardEntry[];
  viewer: LeaderboardViewer | null;
  window: LeaderboardWindow | null;
  pendingApproval: number;
}

/** `GET /recognitions/leaderboard`'s response for the crowns board. */
export interface LeaderboardCrownsResponse {
  board: LEADERBOARD_BOARD.CROWNS;
  period: LEADERBOARD_PERIOD;
  results: LeaderboardCrown[];
}

/**
 * `GET /recognitions/leaderboard`'s query, discriminated on `board` -- mirrors the server-side
 * `GetLeaderboardQuery` except web's own `LEADERBOARD_BOARD` (above) has no `TEAM` member (D3).
 * `limit` is only ever accepted on received/given, matching the api's own `.strict()` schema (crowns'
 * branch has no such field).
 *
 * L4/L5 (923-core-context context/engineering/spark-rewards/phase-51-leaderboard-to-design.md): `core_value_id` is likewise only ever accepted on
 * received/given -- the api's own `.strict()` schema 400s a stray one on `crowns`/`team` rather than
 * silently dropping it (`leaderboard.schemas.ts`'s own comment), so the CROWNS branch below gains no
 * such field, the same compile-time guarantee D3 already gives `board: "team"`.
 */
export type GetLeaderboardQuery =
  | { board: LEADERBOARD_BOARD.RECEIVED; period: LEADERBOARD_PERIOD; limit?: number; core_value_id?: number }
  | { board: LEADERBOARD_BOARD.GIVEN; period: LEADERBOARD_PERIOD; limit?: number; core_value_id?: number }
  | { board: LEADERBOARD_BOARD.CROWNS; period: LEADERBOARD_PERIOD };

/**
 * `GET /recognitions/leaderboard/collaborators`'s row (923-core-context context/engineering/spark-rewards/phase-52-spread-your-sparks.md §3/§4 S2) --
 * up to four teammates the viewer has worked closely with recently (Harvest co-project hours, api-side)
 * and has not yet recognised this quarter. No `period`/`board` on the request: the window is a fixed
 * server-side constant, not the board's own period selector (§4 S2's own "the feature should remind
 * you... not anything more complicated"). Reuses `LeaderboardUser` verbatim, same as `LeaderboardCrown`
 * above -- `{ id, first_name, last_name }`, no avatar field.
 *
 * 🚨 Deliberately carries NO `score`/`total_score` field -- removed api-side because it would let a
 * viewer derive a colleague's logged hours. Do not add one back if it ever reappears in a schema read.
 */
export interface SuggestedCollaborator {
  user_id: number;
  user: LeaderboardUser;
  /**
   * The VIEWER's own most recent APPROVED recognition of this person, always from an earlier quarter
   * (the already-recognised filter is "this quarter", the collaboration window is a rolling 90 days --
   * two different questions, see the phase doc §4 S2). `null` means the viewer has never recognised
   * them -- maps to the card's "never recognised by you" caption; a date maps to "last recognised in
   * <Month>" (see `lastRecognisedCaption`).
   */
  last_recognised_at: string | null;
}

/** `GET /recognitions/leaderboard/collaborators`'s response envelope -- 0-4 rows, no `total`. */
export interface SuggestedCollaboratorsResponse {
  results: SuggestedCollaborator[];
}

/**
 * `RecognitionBestQuarter` on the wire (923-core-context context/engineering/spark-rewards/phase-51-leaderboard-to-design.md L8,
 * `api/app/services/recognition.summary.ts`) -- the viewer's own highest-scoring quarter ever, among
 * their (received, approved) recognitions. `year`/`quarter` come back RAW, not a formatted "Q2 2026"
 * string -- formatting is presentation and belongs to `bestQuarterCopy.ts`, not this model.
 */
export interface AllTimeBestQuarter {
  year: number;
  quarter: number;
  score: number;
}

/**
 * `GET /recognitions/leaderboard/all-time-summary`'s response (923-core-context context/engineering/spark-rewards/phase-51-leaderboard-to-design.md L8,
 * api half) -- viewer-scoped, no query params, matching `SuggestedCollaboratorsRequestSchema`'s own
 * self-only shape above. Backs two of the "Your all-time" rail card's four figures; the other two
 * (lifetime Sparks, all-time rank) already exist elsewhere (`SparksSummary.lifetimeEarned`, and
 * `LeaderboardViewer.rank` over `board=received&period=All-time`) and are deliberately NOT duplicated
 * here.
 *
 * Both fields `.nullable()` on the wire, never zeroed or omitted -- someone with zero approved,
 * non-deleted received recognitions has no best quarter and no first-Spark date at all, which is a
 * different fact from "scored a 0" or "received on the epoch". Render an honest placeholder for
 * `null`, never a fabricated number or date (`bestQuarterCopy.ts`'s own comment).
 */
export interface AllTimeSummary {
  bestQuarter: AllTimeBestQuarter | null;
  firstSparkReceivedAt: string | null;
}
